import React, { FunctionComponent, ReactElement, useContext } from 'react';
import { Row, Col } from 'antd';
import { keyBy } from 'lodash';
import { PositionTemplateFieldType, Resources } from '@erecruit/types';
import { Intl } from '@erecruit/core';
import { H1, Label, Text } from '@erecruit/ui-kit';
import { Disclaimer } from '../Disclaimer';
import { currencySuffix, currencySymbol } from '../BillingStep';
import { WizardContext, PositionField } from '../../types';
import DynamicFormItems from '../DynamicFormItems/DynamicFormItems';
import { useWorksite } from '../DynamicFormItems/helpers';
import { durationUnitResolutions } from '../ScheduleStep/helpers';

const { FormattedMessage, FormattedDate, injectIntl } = Intl;

export interface ReviewStepProps {
  fields: PositionField[];
}

interface DataRowProps {
  label: Intl.LocalizationKeysArray | string;
  value?: ReactElement | string | number | null;
}

export const DataRow: FunctionComponent<DataRowProps> = ({ label, value }) =>
  !value ? null : (
    <Row className="data-row">
      <Col span={12}>
        <Label uppercase>
          {Array.isArray(label) ? <FormattedMessage id={label} /> : label}
        </Label>
      </Col>
      <Col className="data-value" span={12}>
        <Text color="labelPrimary">{value}</Text>
      </Col>
    </Row>
  );

const ReviewStep: FunctionComponent<ReviewStepProps &
  Intl.InjectedIntlProps> = ({ intl, fields }) => {
  const keyFields = keyBy(fields, 'fieldType');

  const formFieldsValues = useContext(WizardContext);

  const {
    positionTitle,
    numberOfPositions,
    positionType,
  } = formFieldsValues.general;

  const {
    estimatedStartDate,
    estimatedEndDate,
    durationAmount,
    durationUnit,
  } = formFieldsValues.schedule;

  const { targetBillRate, estimatedSpend } = formFieldsValues.billing;
  const { worksiteAddress } = formFieldsValues.location;
  const facilityDepartments =
    formFieldsValues.location.facilityDepartments || [];

  const positionTypeField = keyFields[PositionTemplateFieldType.PositionType];
  const worksiteAddressField =
    keyFields[PositionTemplateFieldType.WorksiteAddress];
  const facilityDepartmentsField =
    keyFields[PositionTemplateFieldType.FacilityDepartments];

  const allFacilityDepartments =
    facilityDepartmentsField &&
    facilityDepartmentsField.data &&
    (facilityDepartmentsField.data as Resources.FacilityDepartment[]);
  const positionTypes =
    positionTypeField &&
    positionTypeField.data &&
    (positionTypeField.data as Resources.PositionType[]);

  const { worksite } = useWorksite(worksiteAddressField, worksiteAddress);

  const selectedFacilityDepartments = facilityDepartments.map(
    selectedFacilityDepartmentId => {
      return (
        allFacilityDepartments &&
        allFacilityDepartments.find(
          fd => String(fd.id) === String(selectedFacilityDepartmentId),
        )
      );
    },
  );
  const selectedPositionType = positionTypes?.find(
    (pt: Resources.PositionType) => pt.id === positionType,
  );

  const positionTypeName = selectedPositionType
    ? selectedPositionType.name
    : null;

  const facilityDepartmentsString = selectedFacilityDepartments
    .map(fd => fd?.name)
    .join(', ');

  const durationMessageId = durationUnitResolutions.find(
    ({ value }) => value === durationUnit,
  )?.messageId;
  const data: DataRowProps[] = [
    {
      label: messageIds.vms.newPositionWizard.review.positionTitle,
      value: positionTitle,
    },
    {
      label: messageIds.vms.newPositionWizard.review.positionEstimatedStartDate,
      value: estimatedStartDate && (
        <FormattedDate value={estimatedStartDate.toDate()} />
      ),
    },
    {
      label: messageIds.vms.newPositionWizard.review.positionEstimatedDuration,
      value:
        durationAmount && durationUnit && durationMessageId ? (
          <FormattedMessage
            id={durationMessageId}
            values={{ value: durationAmount }}
          ></FormattedMessage>
        ) : (
          '-'
        ),
    },
    {
      label: messageIds.vms.newPositionWizard.review.positionEstimatedEndDate,
      value: estimatedEndDate && (
        <FormattedDate value={estimatedEndDate.toDate()} />
      ),
    },
    {
      label: messageIds.vms.newPositionWizard.review.positionNumber,
      value: numberOfPositions,
    },
    {
      label: messageIds.vms.newPositionWizard.review.positionType,
      value: positionTypeName,
    },
    {
      label: messageIds.vms.newPositionWizard.review.positionWorksite,
      value: worksite?.name,
    },
    {
      label: messageIds.vms.newPositionWizard.review.positionDepartments,
      value: facilityDepartmentsString,
    },
    {
      label: messageIds.vms.newPositionWizard.review.positionTargetBillRate,
      value:
        targetBillRate &&
        `${currencySymbol(intl)} ${targetBillRate} ${currencySuffix(intl)}`,
    },
    {
      label: messageIds.vms.newPositionWizard.review.positionEstimatedSpend,
      value: estimatedSpend && `${currencySymbol(intl)} ${estimatedSpend}`,
    },
  ];

  return (
    <div>
      <H1>
        <FormattedMessage id={messageIds.vms.newPositionWizard.review.title} />
      </H1>
      <Disclaimer>
        <FormattedMessage
          id={messageIds.vms.newPositionWizard.review.disclaimer}
        />
      </Disclaimer>

      {data.map(
        ({ label, value }, index) =>
          value && <DataRow key={index} label={label} value={value} />,
      )}

      <DynamicFormItems section="review" fields={fields} />
    </div>
  );
};

export default injectIntl(ReviewStep);
